import { IntegrationId } from 'src/shared/types/integration.types';

export type MessageFetchParams = {
  limit?: number;
  inbox_ids?: IntegrationId[];
  before_ts?: number;
  after_ts?: number;
  max_participants?: number;
  min_participants?: number;
  max_messages?: number;
  min_messages?: number;
  responded?: boolean;
  resolved?: boolean;
  min_response_time?: number;
  max_response_time?: number;
  min_resolution_time?: number;
  max_resolution_time?: number;
  approvals_only?: boolean;
  queryString?: string;
  facets?: {
    [facetId: string]: string[];
  };
  entities?: {
    [entityId: string]: string[];
  };
  is_routed?: boolean;
  correct_routed?: boolean;
  // Advanced search fields (POST search v2)
  threadId?: string;
  messageId?: string;
  from?: string[];
  to?: string[];
};

export type MessageType = {
  thread_id: string;
  integration_id: IntegrationId;
  message_id?: string; // Message ID from skeleton data (for lazy loading fallback)
  last_message_id?: string;
  message?: EmailMessage; // Message can be undefined if the message was deleted
  messageError?: string; // Error message if lazy loading failed
  time: number; // Timestamp from analytics (always available)
  message_count: number; // Number of messages in the thread (always available)
  attachment?: {
    contentBytes: string;
    contentType: string;
    id: string;
    name: string;
    size: number;
  };
  fullThread?: EmailMessage[];
  facets: Record<string, string[] | NerModelPredictions[]>; // incoming facets => dirty object
  models: Record<string, string[]>; // cleaned facets => only simple facets (string[])
  entities: Entities;

  actuals: {
    [modelId: string]: BaseActualsMeta;
  };

  actions: MessageActions[];
  groupedActions: {
    [flowId: string]: MessageActions[];
  };

  attachments: EmailAttachment[];

  facets_versions?: Record<string, string>; // version of the models, semver. If version is "actual", tag is verified
  meta?: ActualsMeta;
};

export type ActualsUpdateType = 'VERIFY' | 'VERIFY_ALL' | 'UPDATE' | 'REMOVE' | 'ADD';

export interface BaseActualsMeta {
  type: 'TAG' | 'ENTITY';
  model_type?: 'ner';
  verified?: boolean;
}

export interface ActualsMetaNonNer extends BaseActualsMeta {
  type: 'TAG';
  updates: ActualsUpdate<string>[];
}

export interface ActualsMetaNer extends BaseActualsMeta {
  model_type: 'ner';
  updates?: {
    [tagPath: string]: ActualsUpdate<EntityValue>[];
  };
}

export type ActualsUpdate<T> = {
  action: ActualsUpdateType;
  oldValues: T;
  newValues: T;
  user: {
    id: string | undefined;
    name: string | undefined;
    email: string | undefined;
  };
  timestamp: string;
};

export type ActualsMeta = ActualsMetaNonNer | ActualsMetaNer;

export type EntityValue = Entities[string] | Entities[string][] | string[] | null;

export type Entities = {
  [facetId: string]: {
    [tagId: string]: EntityValue;
  };
};

export function isEntityObject(value: EntityValue | string | undefined): value is Entities[string] {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export type MessageActions = {
  action_id: string;
  flow_id: string;
  action_props: Record<string, any>;
};

export type EmailFacets = {
  [modelId: string]: string[] | NerModelPredictions[];
};

export type NerModelPredictions = { key: string; value: string[] };

export type EmailMessage = {
  messageId: string;
  threadId: string;
  body: string;
  splitBody: {
    content: string;
    header: string;
  }[];
  subject: string;
  timestamp: number;
  attachment: boolean; // legacy, use attachments array instead
  attachments?: EmailAttachment[]; // attachments array for thread messages
  sender: EmailAddress;
  receiver: EmailAddress[];
  bcc: EmailAddress[];
  cc: EmailAddress[];
  folderId: string;
  folderName: string;
  tags: string[];
  headers: Record<string, any>;
  open: {
    web: string;
  };
};

export type EmailAttachment = {
  id: string;
  name: string;
  size: number;
  // It seems super weird to have a nullable contentType, but it has occurred in the wild.
  // At time of writing, the backend has an `any` return type for the api call which returns this,
  // so we're just going to be safe and type as nullable to prevent further crashes on important modules.
  // Ideally in the future `contentType` is not nullable because it doesn't make sense.
  contentType: string | null;
  contentBytes?: string;
};

export type EmailAddress = {
  id: string;
  name: string;
};
